import { DateTime } from "luxon";
import { EngagementLevel } from "../activity/ActivityCalendarDay";
import { ComplianceType, LocationGroupType } from "@src/types.generated";
import { onlyFirstLetterCapitalized } from "@src/lib/utils/strings";
import { DeactivationReason } from "@src/types.generated";
import { RoleAutomationFragment } from "@src/features/roles/role-detail/RoleDetail.generated";
import { ContentType } from "@src/features/contentLibrary/ContentAvatar";
import { Route } from "nextjs-routes";
import { AutomationLocFragment } from "@src/lib/contexts/LocationDetailContext/LocationDetailContext.generated";
import { TrainingSessionFragment } from "@src/features/users/user-details/UserDetailActivity.generated";
import {
  UserDetailComplianceCertificatesTabComplianceAttemptFragment,
  UserDetailComplianceCertificatesTabComplianceRecordFragment,
} from "@src/features/users/assignments/UserDetailComplianceCertificatesTab.generated";
import {
  ASSIGNED_USER_TYPE_COPY,
  getUSStateLabel,
} from "@src/features/sexualHarassmentPrevention/utils";
import { orderBy } from "lodash";
import { format, parseISO } from "date-fns";
import { UserDetailAssignmentsAssignmentFragment } from "@src/features/users/assignments/UserDetailAssignments.generated";

export function getAutomationContentName(
  automation: RoleAutomationFragment | AutomationLocFragment,
): string {
  return (
    automation.libraryItem?.orgPremiumContentConfig?.premiumContentConfig
      .catalogModule?.module?.libraryItem?.name.en ||
    automation.libraryItem.name.en
  );
}

export function getAutomationContentId(
  automation: RoleAutomationFragment | AutomationLocFragment,
): string | number {
  return (
    automation.libraryItem?.course?.id ||
    automation.libraryItem?.module?.id ||
    automation.libraryItem?.trainingResource?.id ||
    ""
  );
}

export function getAutomationContentType(automation: {
  libraryItem: RoleAutomationFragment["libraryItem"];
}): ContentType {
  return automation.libraryItem?.course
    ? "Course"
    : automation.libraryItem?.module
      ? "Module"
      : automation.libraryItem?.orgPremiumContentConfig
        ? "PremiumModule"
        : "Resource";
}

export function getDeactivatedByCopy(e: {
  deactivatedReason?: DeactivationReason | null;
  deactivatedBy?: { name: string } | null;
}): string {
  if (e.deactivatedReason === DeactivationReason.Hris) {
    return "by HRIS integration";
  } else if (e.deactivatedReason === DeactivationReason.SwitchedOrgs) {
    return "by switching companies";
  } else if (e.deactivatedReason === DeactivationReason.Manual) {
    return "manually" + (e.deactivatedBy ? " by " + e.deactivatedBy?.name : "");
  } else if (e.deactivatedReason === DeactivationReason.DeactivatedOrg) {
    return "when the company was deactivated";
  } else {
    return "";
  }
}

type DidEarnCertificateAssignment = {
  currentCourseInstance?:
    | {
        certificateUrl?: string | null | undefined;
      }
    | null
    | undefined;
};

export const getCourseCertificateUrl = (
  assignment: DidEarnCertificateAssignment,
): string | null => {
  const ci = assignment.currentCourseInstance;
  return ci?.certificateUrl || null;
};

export const getAssignmentCertificateUrl = (
  assignment: UserDetailAssignmentsAssignmentFragment,
): string | null | undefined => {
  if (assignment.currentCourseInstance) {
    return assignment.currentCourseInstance.certificateUrl;
  }
  return assignment.certificateUrl;
};

export const formatComplianceTypeNames = (
  complianceType: ComplianceType,
): string => {
  const capitalizedWords = complianceType.split("_").map((word) => {
    return onlyFirstLetterCapitalized(word);
  });

  return capitalizedWords.join(" ");
};

export const getPersonDetailRoute = (userId: number): Route => {
  return {
    pathname: "/users/[id]",
    query: {
      id: userId.toString(),
    },
  };
};

export const getRoleDetailRoute = (roleId: number): Route => {
  return {
    pathname: "/roles/[id]",
    query: {
      id: roleId.toString(),
    },
  };
};

export const getLocationDetailRoute = (locationId: number): Route => {
  return {
    pathname: "/locations/[id]",
    query: {
      id: locationId.toString(),
    },
  };
};

export const getLocationGroupDetailRoute = (
  locationGroupId: string,
  locationGroupType: LocationGroupType,
): Route => {
  if (locationGroupType === LocationGroupType.Region) {
    return {
      pathname: "/locations/regions/[id]",
      query: {
        id: locationGroupId,
      },
    };
  } else if (locationGroupType === LocationGroupType.Brand) {
    return {
      pathname: "/locations/brands/[id]",
      query: {
        id: locationGroupId,
      },
    };
  } else {
    return {
      pathname: "/locations/franchises/[id]",
      query: {
        id: locationGroupId,
      },
    };
  }
};

export const getRoleGroupDetailRoute = (roleGroupId: string): Route => {
  return {
    pathname: "/roles/departments/[id]",
    query: {
      id: roleGroupId,
    },
  };
};

export const getTagRoute = (tagId: string): Route => {
  return {
    pathname: "/library",
    query: {
      type: "modules",
      tagIds: tagId,
    },
  };
};

export const getEngagementLevel = (
  sessions: TrainingSessionFragment[],
): EngagementLevel => {
  const seconds = sessions.reduce((accum, session) => {
    return (
      accum +
      DateTime.fromISO(session.endTime).diff(
        DateTime.fromISO(session.startTime),
        "seconds",
      ).seconds
    );
  }, 0);

  if (seconds === 0) {
    return 0;
  } else if (seconds < 60) {
    return 1;
  } else if (seconds < 120) {
    return 2;
  } else if (seconds < 180) {
    return 3;
  } else {
    return 4;
  }
};

export const filterEnabledAutomations = (
  automations: RoleAutomationFragment[] | AutomationLocFragment[],
): RoleAutomationFragment[] | AutomationLocFragment[] => {
  return automations.filter((automation) => automation.enabled);
};

export const getComplianceRecordCertificateUrl = (
  x: UserDetailComplianceCertificatesTabComplianceRecordFragment,
): string | null | undefined => {
  const attempt = getMostRecentComplianceAttempt(x.attempts);
  return (
    attempt?.moduleInstance?.certificateUrl ||
    attempt?.courseInstance?.certificateUrl ||
    attempt?.externalCertificateUrl
  );
};

export const getComplianceRecordAssignedUserType = (
  x: UserDetailComplianceCertificatesTabComplianceRecordFragment,
): string | undefined | null => {
  const attempt = getMostRecentComplianceAttempt(x.attempts);
  if (!attempt || !attempt.assignedUserType) return null;
  return ASSIGNED_USER_TYPE_COPY[attempt.assignedUserType];
};

export const getComplianceRecordStartDate = (
  x: UserDetailComplianceCertificatesTabComplianceRecordFragment,
): string | null => {
  return x.user.hrisEmployee?.startDate
    ? format(parseISO(x.user.hrisEmployee.startDate), "M/d/yyyy")
    : null;
};

export const getMostRecentComplianceAttempt = (
  x: UserDetailComplianceCertificatesTabComplianceAttemptFragment[],
): UserDetailComplianceCertificatesTabComplianceAttemptFragment | undefined => {
  return orderBy(
    x.filter((c) => c.compliantUntil),
    "createdAt",
    "desc",
  )[0];
};

export const getComplianceRecordUsState = (
  x: UserDetailComplianceCertificatesTabComplianceRecordFragment,
): string => {
  const attempt = getMostRecentComplianceAttempt(x.attempts);
  return getUSStateLabel(attempt?.usState);
};
